import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { getPedido, PedidoDetalhe } from '../api';
import { downloadCsv, fmtDataHora, fmtDataHoraFoto, fmtDias, fmtMoney2, fmtNum, fmtPct } from '../utils/format';

export default function PedidoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<PedidoDetalhe | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    getPedido(id!).then(setData).catch((e: Error) => setErro(e.message));
  }, [id]);

  if (erro) return <div className="card"><p className="empty">{erro}</p><Link to="/service-level/dashboard">← Voltar</Link></div>;
  if (!data) return <p className="empty">Carregando pedido…</p>;

  const { pedido: p, seller, lojista, sla, itens } = data;
  const totQtd = itens.reduce((s, i) => s + i.qtd_comprada, 0);
  const totEntregue = itens.reduce((s, i) => s + i.qtd_entregue, 0);
  const totNf = itens.reduce((s, i) => s + i.valor_nf_item, 0);
  const totCorte = itens.reduce((s, i) => s + i.corte_item, 0);
  const totComprado = itens.reduce((s, i) => s + i.preco_compra_unit * i.qtd_comprada, 0);

  const exportar = () => {
    downloadCsv(
      `pedido_${p.pedido_id}.csv`,
      ['EAN', 'Produto', 'Indústria', 'BU', 'Qtd comprada', 'Qtd entregue', '% atendimento', 'Preço compra (R$)', 'Valor NF (R$)', 'Corte (R$)'],
      itens.map((i) => [
        i.ean, i.descricao_produto, i.industria_nome ?? '', i.bu_sigla ?? '', i.qtd_comprada, i.qtd_entregue,
        i.qtd_comprada > 0 ? ((i.qtd_entregue / i.qtd_comprada) * 100).toFixed(1) : '',
        i.preco_compra_unit.toFixed(2), i.valor_nf_item.toFixed(2), i.corte_item.toFixed(2),
      ])
    );
  };

  return (
    <>
      <div className="page-header">
        <div>
          <button className="btn secondary mb-2" onClick={() => navigate(-1)}><ArrowLeft size={14} /> Voltar</button>
          <h1>{p.pedido_id}</h1>
          <div className="sub">
            <Link to={`/service-level/sellers/${seller.seller_id}`}>{seller.nome_fantasia}</Link> · {p.regiao}
          </div>
          {data.visao_restrita && (
            <div className="mt-2">
              <span className="seller-chip" title="Seu perfil vê apenas os itens e valores das indústrias sob sua responsabilidade">
                Visão restrita: {data.visao_restrita.join(', ')}
              </span>
            </div>
          )}
        </div>
        <div className="filters">
          <span className="foto-badge">Última atualização {fmtDataHoraFoto(data.ts_foto)}</span>
          <button className="btn" onClick={exportar}><Download size={14} /> Exportar pedido</button>
        </div>
      </div>

      <div className="card">
        <h2>Cabeçalho</h2>
        <div className="pedido-header">
          <div className="field"><div className="fl">Lojista</div><div className="fv">{lojista.razao_social}</div>
            <div className="muted text-xs">{lojista.cnpj} · {lojista.cidade}/{lojista.uf}</div></div>
          <div className="field"><div className="fl">Status</div>
            <div className="fv"><span className={`badge ${p.status_atual}`}>{p.status_atual.replace('_', ' ')}</span></div></div>
          <div className="field"><div className="fl">Pedido</div><div className="fv">{fmtDataHora(p.data_pedido)}</div></div>
          <div className="field"><div className="fl">Recebimento</div><div className="fv">{fmtDataHora(p.data_recebimento)}</div></div>
          <div className="field"><div className="fl">Envio</div><div className="fv">{p.data_envio ? fmtDataHora(p.data_envio) : '— não enviado'}</div></div>
          <div className="field"><div className="fl">Finalização</div><div className="fv">{p.data_finalizacao ? fmtDataHora(p.data_finalizacao) : '—'}</div></div>
          <div className="field"><div className="fl">SLA aplicável</div>
            <div className="fv">
              {sla.prazo_horas / 24} {sla.prazo_horas === 24 ? 'dia' : 'dias'} {sla.tipo_contagem === 'UTEIS' ? 'úteis' : 'corridos'}{' '}
              {!sla.configurado && <span className="badge default-sla">SLA não configurado — default</span>}
            </div></div>
          <div className="field"><div className="fl">Dias em atraso</div>
            <div className={`fv ${p.flag_p1 ? 'text-critical' : ''}`}>
              {p.flag_p1 ? fmtDias(p.horas_em_atraso) : '—'}
            </div></div>
        </div>
        <div className="mt-3.5 flex gap-1.5 flex-wrap">
          {p.flag_p1 && <span className="badge p1">P1 · Pedido parado/atrasado</span>}
          {p.flag_p2 && <span className="badge p2">P2 · Entrega parcial ({fmtPct(p.pct_atendimento_qtd, 0)} atendido)</span>}
          {p.flag_p3 && <span className="badge p3">P3 · Corte de {fmtMoney2(p.valor_corte)}</span>}
        </div>
      </div>

      <div className="card">
        <h2>Itens do pedido — comprado × entregue × NF (por EAN)</h2>
        <div className="table-wrap">
          <table className="min-w-[820px]">
            <thead>
              <tr>
                <th>EAN</th><th>Produto</th><th>Indústria · BU</th>
                <th className="num">Qtd comprada</th><th className="num">Qtd entregue</th>
                <th className="num">% atend.</th><th className="num">Preço compra</th>
                <th className="num">Valor NF</th><th className="num">Corte</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i) => {
                const pct = i.qtd_comprada > 0 ? i.qtd_entregue / i.qtd_comprada : null;
                return (
                  <tr key={i.ean}>
                    <td className="muted">{i.ean}</td>
                    <td>{i.descricao_produto}</td>
                    <td className="muted">{i.industria_nome}{i.bu_sigla ? ` · ${i.bu_sigla}` : ''}</td>
                    <td className="num">{fmtNum(i.qtd_comprada)}</td>
                    <td className={`num ${i.qtd_entregue < i.qtd_comprada ? 'text-critical' : ''}`}>
                      {fmtNum(i.qtd_entregue)}
                    </td>
                    <td className="num">{fmtPct(pct, 0)}</td>
                    <td className="num">{fmtMoney2(i.preco_compra_unit)}</td>
                    <td className="num">{fmtMoney2(i.valor_nf_item)}</td>
                    <td className={`num ${i.corte_item > 0 ? 'text-critical' : ''}`}>
                      {i.corte_item > 0 ? fmtMoney2(i.corte_item) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-bold">
                <td colSpan={3}>Total · {fmtMoney2(totComprado)} comprados</td>
                <td className="num">{fmtNum(totQtd)}</td>
                <td className="num">{fmtNum(totEntregue)}</td>
                <td className="num">{fmtPct(totQtd > 0 ? totEntregue / totQtd : null, 0)}</td>
                <td className="num" />
                <td className="num">{fmtMoney2(totNf)}</td>
                <td className={`num ${totCorte > 0 ? 'text-critical' : ''}`}>{fmtMoney2(totCorte)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
}
